import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Bell,
  CheckCircle2,
  Receipt,
  RefreshCcw,
  ShieldCheck,
  TrendingUp,
  Users,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import Logo from '@/components/common/Logo';
import { Badge, Button, Card } from '@/components/ui';
import { APP_NAME, ROUTES } from '@/utils/constants';

const FEATURES = [
  {
    icon: Users,
    title: 'Lead Management',
    body: 'Capture from web, WhatsApp, webinars, Excel and API. Auto-assign by pincode.',
  },
  {
    icon: TrendingUp,
    title: 'Sales Workflow',
    body: 'Pipeline, quotations, T&C tracking, payment capture in one flow.',
  },
  {
    icon: Receipt,
    title: 'Billing',
    body: 'Invoice generation, GST/TDS, payment verification and email queue.',
  },
  {
    icon: Wrench,
    title: 'Service',
    body: 'Onboarding tasks, welcome emails, implementation tracking, SLAs.',
  },
  {
    icon: RefreshCcw,
    title: 'Renewals',
    body: 'Renewal calendar, reminders, payments, and history.',
  },
  {
    icon: BarChart3,
    title: 'MIS & Reports',
    body: 'Funnel, revenue, product, RM and region analytics.',
  },
  {
    icon: Bell,
    title: 'Notifications',
    body: 'Email, in-app and audit-grade event log across modules.',
  },
  {
    icon: ShieldCheck,
    title: 'Security',
    body: 'Role-based access, MFA, refresh-token rotation, audit trail.',
  },
];

const FLOW = ['Lead Capture', 'Sales', 'Billing', 'Service', 'Renewal', 'MIS'];

const HIGHLIGHTS = ['Role-based access', 'Audit-grade log', 'Single source of truth'];

const PREVIEW_METRICS = [
  { label: 'Conversion', value: '26.6%', trend: '+1.4pp' },
  { label: 'Active leads', value: '1,284', trend: '+8%' },
  { label: 'Revenue MTD', value: '₹48.6L', trend: '+15%' },
];

const PREVIEW_CHART = [40, 55, 35, 70, 50, 80, 95];

const LandingPage = () => (
  <div className="min-h-screen bg-white">
    <header className="sticky top-0 z-40 border-b border-line bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Logo />
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" className="min-h-11 sm:min-h-0">
            <Link to={ROUTES.LOGIN}>Login</Link>
          </Button>
          <Button asChild size="sm" className="min-h-11 sm:min-h-0 gap-1.5">
            <Link to={ROUTES.REGISTER}>
              Request Access
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </header>

    <main>
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-2 lg:items-center lg:gap-12 lg:px-8 lg:py-24">
          <div>
            <Badge variant="info" className="normal-case">
              <span className="mr-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
              Internal CRM · v0.1.0
            </Badge>
            <h1 className="mt-5 text-3xl font-extrabold leading-[1.1] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              One workbench for the entire AdvisorKhoj operation.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/60">
              Replace fragmented Excel tracking with a single connected journey, from lead capture
              to sales conversion, billing, service implementation, renewals and MIS reporting.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button asChild size="lg" className="min-h-11 sm:min-h-0 w-full gap-2 sm:w-auto">
                <Link to={ROUTES.LOGIN}>
                  Login to CRM
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="min-h-11 sm:min-h-0 w-full sm:w-auto"
              >
                <Link to={ROUTES.REGISTER}>Request Access</Link>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink/60">
              {HIGHLIGHTS.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="relative">
            <Card padding="none" className="overflow-hidden">
              <div className="flex items-center justify-between border-b border-line bg-cream px-4 py-2.5">
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-danger" />
                  <span className="h-2.5 w-2.5 rounded-full bg-warning" />
                  <span className="h-2.5 w-2.5 rounded-full bg-success" />
                </div>
                <span className="hidden text-[10px] font-semibold uppercase tracking-wider text-ink/60 sm:block">
                  crm.advisorkhoj/home
                </span>
                <span className="w-12" />
              </div>
              <div className="space-y-4 p-4 sm:p-5">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-ink/60">
                    Sales RM dashboard
                  </div>
                  <div className="mt-1 text-sm font-bold text-ink">Good morning, Priya</div>
                </div>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                  {PREVIEW_METRICS.map((m) => (
                    <div key={m.label} className="rounded-lg border border-line bg-white p-2.5">
                      <div className="text-[9px] font-semibold uppercase tracking-wider text-ink/60">
                        {m.label}
                      </div>
                      <div className="mt-1 text-base font-extrabold tabular-nums text-ink">
                        {m.value}
                      </div>
                      <div className="mt-1 inline-flex items-center gap-0.5 text-[9px] font-bold text-success">
                        <ArrowUpRight className="h-2.5 w-2.5" />
                        {m.trend}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-line bg-white p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-ink/60">
                      Revenue trend · 7d
                    </div>
                    <Badge variant="success" className="text-[9px] normal-case">
                      Live
                    </Badge>
                  </div>
                  <div className="mt-2 flex h-16 items-end gap-1.5">
                    {PREVIEW_CHART.map((h) => (
                      <div
                        key={h}
                        className="flex-1 rounded-sm bg-brand"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-line bg-cream p-2.5">
                  {FLOW.map((step, i) => (
                    <span key={step} className="flex items-center gap-1.5">
                      <span className="rounded-md border border-line bg-white px-2 py-0.5 text-[10px] font-bold text-ink shadow-sm">
                        {step}
                      </span>
                      {i < FLOW.length - 1 && <span className="text-ink/30">›</span>}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-cream">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <Badge variant="neutral" className="normal-case">
                Modules
              </Badge>
              <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-ink sm:text-4xl">
                Everything connected, nothing lost.
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-ink/60">
                Eight modules built to mirror exactly how the {APP_NAME} team runs operations day to
                day.
              </p>
            </div>
          </div>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {FEATURES.map((f) => (
              <Card key={f.title} className="transition-colors hover:border-brand/40">
                <div className="grid h-10 w-10 place-items-center rounded-xl border border-brand/20 bg-brand-tint text-brand">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-bold tracking-tight text-ink">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink/60">{f.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
          <Card className="relative overflow-hidden bg-brand text-white">
            <div className="flex flex-wrap items-center justify-between gap-6 p-6 sm:p-8 lg:p-12">
              <div>
                <h2 className="text-xl font-extrabold tracking-tight sm:text-3xl">
                  Ready to retire the Excel sheets?
                </h2>
                <p className="mt-2 max-w-lg text-sm text-white/80">
                  Login with your {APP_NAME} email and explore the live demo dashboard.
                </p>
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <Button
                  asChild
                  size="lg"
                  variant="secondary"
                  className="min-h-11 sm:min-h-0 w-full focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand sm:w-auto"
                >
                  <Link to={ROUTES.LOGIN}>Login</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="ghost"
                  className="min-h-11 sm:min-h-0 w-full border border-white/40 text-white hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand sm:w-auto"
                >
                  <Link to={ROUTES.REGISTER}>Request Access</Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </main>

    <footer className="border-t border-line bg-white">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-1 px-4 py-5 text-xs text-ink/60 sm:flex-row sm:flex-wrap sm:justify-between sm:gap-2 sm:px-6 lg:px-8">
        <span>
          ©{new Date().getFullYear()} {APP_NAME} · Internal use only
        </span>
        <a
          className="-my-2 inline-flex min-h-11 items-center rounded px-1 py-2 underline underline-offset-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          href="mailto:support@advisorkhoj.com"
        >
          support@advisorkhoj.com
        </a>
        <span>v0.1.0</span>
      </div>
    </footer>
  </div>
);

export default LandingPage;
